import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, Tag, X, Loader2, ExternalLink } from "lucide-react";

// Numeric Apple App ID (App Store Connect → App Information)
const APPLE_APP_ID = "6756681577";

const buildRedeemUrl = (offerCode: string) =>
  `https://apps.apple.com/redeem?ctx=offercodes&id=${APPLE_APP_ID}&code=${encodeURIComponent(offerCode)}`;

type AppliedCode = {
  code: string;
  creator_name: string;
  is_apple_offer_code: boolean;
  apple_offer_code_id: string | null;
};

export function CreatorCodeInput() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState<AppliedCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);


  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("creator_code_redemptions")
        .select("code")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data?.code) {
        const { data: cc } = await supabase
          .from("creator_codes")
          .select("code, creator_name, is_apple_offer_code, apple_offer_code_id")
          .ilike("code", data.code)
          .maybeSingle();
        if (cc) setApplied(cc as AppliedCode);
      }
      setChecking(false);
    })();
  }, [user]);

  const openRedeem = (offerCode: string) => {
    const url = buildRedeemUrl(offerCode);
    // Despia webviews block programmatic navigation to some schemes; try
    // window.open first (works natively + web), then hard-navigate as fallback.
    try {
      const win = window.open(url, "_blank");
      if (win) return;
    } catch {
      /* fall through */
    }
    try {
      window.location.href = url;
    } catch {
      toast.error("Could not open the App Store", { description: `Redeem code: ${offerCode}` });
    }
  };


  const apply = async () => {
    if (!user) return;
    const clean = code.trim().toUpperCase();
    if (!clean) return;
    setLoading(true);
    const { data: cc } = await supabase
      .from("creator_codes")
      .select("code, creator_name, is_active, is_apple_offer_code, apple_offer_code_id")
      .ilike("code", clean)
      .maybeSingle();
    if (!cc || !cc.is_active) {
      toast.error("Invalid creator code");
      setLoading(false);
      return;
    }
    const { error } = await supabase
      .from("creator_code_redemptions")
      .upsert(
        { user_id: user.id, code: cc.code, applied_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
    if (error) {
      toast.error("Could not apply code");
      setLoading(false);
      return;
    }
    setApplied(cc as AppliedCode);
    setCode("");
    setLoading(false);

    if (cc.is_apple_offer_code && cc.apple_offer_code_id) {
      toast.success(`Code applied — opening App Store to redeem ${cc.code}`);
      // Small delay so the toast is visible before the app switches contexts
      setTimeout(() => openRedeem(cc.apple_offer_code_id!), 400);
    } else {
      toast.success(`Code applied — supporting ${cc.creator_name}`);
    }
  };

  const remove = async () => {
    if (!user) return;
    await supabase.from("creator_code_redemptions").delete().eq("user_id", user.id);
    setApplied(null);
  };

  if (checking) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-4 h-4 text-primary" />
          <p className="text-sm font-medium">Creator code</p>
        </div>
        {applied ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-primary/20 text-primary border-0">
                  <Check className="w-3 h-3 mr-1" />
                  {applied.code}
                </Badge>
                <span className="text-xs text-muted-foreground">Supporting {applied.creator_name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={remove}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {applied.is_apple_offer_code && applied.apple_offer_code_id && (
              <div className="space-y-2">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => openRedeem(applied.apple_offer_code_id!)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Redeem in App Store
                </Button>
                <p className="text-xs text-muted-foreground">
                  Tap to open the App Store redemption sheet — the discount is applied there on the Yearly plan, not inside the app.
                </p>
              </div>
            )}

          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              maxLength={32}
              className="uppercase"
            />
            <Button onClick={apply} disabled={loading || !code.trim()}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2">
          Support a creator — they'll get credit for your subscription. Some codes unlock an App Store discount.
        </p>
      </CardContent>
    </Card>
  );
}
